import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  listKeyReceipts,
  createKeyReceipt,
  getKeyReceipt,
  patchKeyReceipt,
  addKeyReceiptItem,
  deleteKeyReceiptItem,
  generateKeyReceiptPdf,
  getKeyReceiptPdfUrl,
  signKeyReceiptDual,
  getKeyReceiptSignedPdfUrl,
} from "../../../api/adminData";
import { formatDateOnly, formatDateTime } from "../../../utils/datetime";
import SignaturePad from "./SignaturePad";

/**
 * Key-receipt (Schlüsselquittung) section for one tenancy (K1b).
 *
 * Mirrors RentalContractSection: inline (no modal), sonner toasts, popup-safe
 * PDF flow (open the tab SYNCHRONOUSLY before awaiting). Shows the tenancy's
 * primary key receipt: create it, edit handover metadata + the birthdate that
 * appears on the receipt, manage key items, and generate/open the PDF. All
 * display data is read from the frozen snapshot_* fields the backend returns.
 */

const STATUS_LABELS_DE = {
  draft: "Entwurf",
  generated: "Generiert",
  signed: "Unterzeichnet",
};

function statusLabel(status) {
  return STATUS_LABELS_DE[status] || status || "—";
}

// Exported for the tenancy Dokumente tab — one German wording per status.
export { statusLabel as keyReceiptStatusLabel };

// Frontend suggestion list (backend accepts any non-empty string).
const KEY_TYPE_OPTIONS = [
  ["house_entrance", "Hauseingang"],
  ["apartment_door", "Wohnungstür"],
  ["room_door", "Zimmertür"],
  ["mailbox", "Briefkasten"],
  ["basement", "Keller"],
  ["laundry", "Waschküche"],
  ["bike_room", "Veloraum"],
  ["garage", "Garage"],
  ["badge", "Badge / Chip"],
  ["other", "Sonstiges"],
];
const KEY_TYPE_LABELS = Object.fromEntries(KEY_TYPE_OPTIONS);

function keyTypeLabel(t) {
  return KEY_TYPE_LABELS[t] || t || "—";
}

const EMPTY_ITEM = {
  key_type: "house_entrance",
  label: "",
  quantity: "1",
  key_number: "",
  notes: "",
};

export default function KeyReceiptSection({ tenancyId, onSigned }) {
  // undefined = loading, null = none, object = loaded (with items[])
  const [receipt, setReceipt] = useState(undefined);
  const [listError, setListError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [meta, setMeta] = useState({
    receipt_date: "",
    handover_date: "",
    handover_location: "",
    notes: "",
    snapshot_tenant_birthdate: "",
  });
  const [savingMeta, setSavingMeta] = useState(false);

  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [addingItem, setAddingItem] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);

  const [pdfBusy, setPdfBusy] = useState(false);
  const [opening, setOpening] = useState(false);

  // Dual signature (K2d): tenant + management/landlord representative co-sign in
  // the same session, plus the handover place/date that drives the PDF's
  // "Ort / Datum" line.
  const [signOpen, setSignOpen] = useState(false);
  const [signLocation, setSignLocation] = useState("");
  const [signDate, setSignDate] = useState("");
  const [signName, setSignName] = useState("");
  const [sigEmpty, setSigEmpty] = useState(true);
  const [mgmtName, setMgmtName] = useState("");
  const [mgmtSigEmpty, setMgmtSigEmpty] = useState(true);
  const [signing, setSigning] = useState(false);
  const [openingSigned, setOpeningSigned] = useState(false);
  const sigPadRef = useRef(null);
  const mgmtSigPadRef = useRef(null);

  const items = Array.isArray(receipt?.items) ? receipt.items : [];
  const hasPdf = Boolean(receipt?.pdf_object_key);
  const isSigned = receipt?.status === "signed";
  const hasSignedPdf = Boolean(receipt?.signed_pdf_object_key);

  const load = useCallback(async () => {
    if (!tenancyId) return;
    setListError("");
    try {
      const data = await listKeyReceipts(tenancyId);
      const rows = Array.isArray(data?.items) ? data.items : [];
      if (rows.length === 0) {
        setReceipt(null);
        return;
      }
      const detail = await getKeyReceipt(rows[0].id);
      setReceipt(detail);
      setMeta({
        receipt_date: detail.receipt_date || "",
        handover_date: detail.handover_date || "",
        handover_location: detail.handover_location || "",
        notes: detail.notes || "",
        snapshot_tenant_birthdate: detail.snapshot_tenant_birthdate || "",
      });
    } catch (err) {
      setReceipt(null);
      setListError(err?.message || "Schlüsselquittung konnte nicht geladen werden.");
      toast.error(err?.message || "Schlüsselquittung konnte nicht geladen werden.");
    }
  }, [tenancyId]);

  useEffect(() => {
    setReceipt(undefined);
    load();
  }, [load]);

  const handleCreate = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      await createKeyReceipt(tenancyId, {});
      toast.success("Schlüsselquittung erstellt.");
      await load();
    } catch (err) {
      toast.error(err?.message || "Schlüsselquittung konnte nicht erstellt werden.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveMeta = async () => {
    if (!receipt?.id || savingMeta) return;
    setSavingMeta(true);
    try {
      await patchKeyReceipt(receipt.id, {
        receipt_date: meta.receipt_date || null,
        handover_date: meta.handover_date || null,
        handover_location: meta.handover_location || null,
        notes: meta.notes || null,
        snapshot_tenant_birthdate: meta.snapshot_tenant_birthdate || null,
      });
      toast.success("Gespeichert.");
      await load();
    } catch (err) {
      toast.error(err?.message || "Änderungen konnten nicht gespeichert werden.");
    } finally {
      setSavingMeta(false);
    }
  };

  const handleAddItem = async () => {
    if (!receipt?.id || addingItem) return;
    if (!itemForm.key_type) {
      toast.error("Schlüsselart ist erforderlich.");
      return;
    }
    const qty = parseInt(itemForm.quantity, 10);
    if (!Number.isFinite(qty) || qty < 1) {
      toast.error("Anzahl muss mindestens 1 sein.");
      return;
    }
    setAddingItem(true);
    try {
      await addKeyReceiptItem(receipt.id, {
        key_type: itemForm.key_type,
        label: itemForm.label || null,
        quantity: qty,
        key_number: itemForm.key_number || null,
        notes: itemForm.notes || null,
      });
      toast.success("Position hinzugefügt.");
      setItemForm(EMPTY_ITEM);
      await load();
    } catch (err) {
      toast.error(err?.message || "Schlüsselposition konnte nicht hinzugefügt werden.");
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!receipt?.id || !itemId || deletingItemId === itemId) return;
    setDeletingItemId(itemId);
    try {
      await deleteKeyReceiptItem(receipt.id, itemId);
      toast.success("Position gelöscht.");
      await load();
    } catch (err) {
      toast.error(err?.message || "Schlüsselposition konnte nicht gelöscht werden.");
    } finally {
      setDeletingItemId(null);
    }
  };

  // Open the tab SYNCHRONOUSLY (before awaiting) so the browser does not treat
  // it as a programmatic popup and block it. No "noopener,noreferrer" — noopener
  // can make window.open return null and break the open-then-navigate flow.
  // A small "PDF wird erstellt …" waiting page replaces the white/blank tab the
  // user otherwise stares at while the PDF is generated; writing it is pure UX
  // polish wrapped in try/catch so a mobile-browser failure never breaks the
  // sign/open flow.
  const openBlankWindow = () => {
    const w = window.open("", "_blank");
    if (w) {
      try {
        w.opener = null;
      } catch (_) {
        /* cross-origin / read-only opener — ignore */
      }
      try {
        w.document.write(
          `<!doctype html>
<html>
  <head>
    <title>PDF wird erstellt</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body { font-family: system-ui, sans-serif; padding: 32px; color: #0f172a; }
      .box { max-width: 520px; margin: 12vh auto; line-height: 1.5; }
      .title { font-size: 20px; font-weight: 700; margin-bottom: 8px; }
      .muted { color: #64748b; }
    </style>
  </head>
  <body>
    <div class="box">
      <div class="title">PDF wird erstellt …</div>
      <div class="muted">Bitte warten, das Dokument wird vorbereitet.</div>
    </div>
  </body>
</html>`
        );
        w.document.close();
      } catch (_) {
        /* waiting page is only polish — never break the flow over it */
      }
    }
    return w;
  };

  const handleOpenPdf = async () => {
    if (!receipt?.id || opening) return;
    const pdfWindow = openBlankWindow();
    setOpening(true);
    try {
      const result = await getKeyReceiptPdfUrl(receipt.id);
      if (!result?.url) {
        pdfWindow?.close();
        toast.error("PDF-Link konnte nicht geladen werden.");
        return;
      }
      if (pdfWindow) {
        pdfWindow.location = result.url;
        toast.success("PDF geöffnet.");
      } else {
        toast.error("Popup wurde blockiert. Bitte Popups erlauben.");
      }
    } catch (err) {
      pdfWindow?.close();
      toast.error(err?.message || "PDF konnte nicht geöffnet werden.");
    } finally {
      setOpening(false);
    }
  };

  const handleGenerateAndOpen = async (force) => {
    if (!receipt?.id || pdfBusy) return;
    const pdfWindow = openBlankWindow();
    setPdfBusy(true);
    try {
      await generateKeyReceiptPdf(receipt.id, { force });
      const result = await getKeyReceiptPdfUrl(receipt.id);
      if (!result?.url) {
        pdfWindow?.close();
        toast.error("PDF-Link konnte nicht geladen werden.");
        return;
      }
      if (pdfWindow) {
        pdfWindow.location = result.url;
      } else {
        toast.error("Popup wurde blockiert. Bitte Popups erlauben.");
      }
      toast.success(force ? "PDF neu generiert." : "PDF generiert.");
      await load();
    } catch (err) {
      pdfWindow?.close();
      toast.error(err?.message || "PDF konnte nicht generiert werden.");
    } finally {
      setPdfBusy(false);
    }
  };

  const openSignPanel = () => {
    setSignLocation(receipt?.handover_location || "");
    setSignDate(receipt?.handover_date || "");
    setSignName(receipt?.snapshot_tenant_name || "");
    setMgmtName("");
    setSigEmpty(true);
    setMgmtSigEmpty(true);
    sigPadRef.current?.clear();
    mgmtSigPadRef.current?.clear();
    setSignOpen(true);
  };

  const closeSignPanel = () => {
    if (signing) return;
    setSignOpen(false);
  };

  const signLocationTrim = signLocation.trim();
  const signNameTrim = signName.trim();
  const mgmtNameTrim = mgmtName.trim();
  const canSign =
    !signing &&
    items.length > 0 &&
    Boolean(signLocationTrim) &&
    Boolean(signDate) &&
    Boolean(signNameTrim) &&
    !sigEmpty &&
    Boolean(mgmtNameTrim) &&
    !mgmtSigEmpty;

  // Dual-sign and open the signed PDF. Popup-safe: open the tab SYNCHRONOUSLY
  // before any await. The handover place/date patch runs FIRST and sequentially
  // — a patch failure jumps straight to catch so the irreversible sign-dual is
  // never reached with stale place/date.
  const handleSign = async () => {
    if (!receipt?.id || signing) return;
    const location = signLocationTrim;
    const dateVal = (signDate || "").trim();
    const name = signNameTrim;
    const mName = mgmtNameTrim;

    if (items.length === 0) {
      toast.error("Mindestens eine Schlüsselposition erforderlich.");
      return;
    }
    if (!location) {
      toast.error("Bitte Ort der Übergabe eingeben.");
      return;
    }
    if (!dateVal) {
      toast.error("Bitte Datum der Übergabe eingeben.");
      return;
    }
    if (!name) {
      toast.error("Bitte Namen der Mieterin/des Mieters eingeben.");
      return;
    }
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error("Bitte Mieter-Signatur erfassen.");
      return;
    }
    if (!mName) {
      toast.error("Bitte Namen der Verwaltung/Vertretung eingeben.");
      return;
    }
    if (!mgmtSigPadRef.current || mgmtSigPadRef.current.isEmpty()) {
      toast.error("Bitte Verwaltung/Vertretung unterschreiben lassen.");
      return;
    }
    const dataUrl = sigPadRef.current.toDataUrl();
    const mgmtDataUrl = mgmtSigPadRef.current.toDataUrl();
    if (!dataUrl) {
      toast.error("Bitte Mieter-Signatur erfassen.");
      return;
    }
    if (!mgmtDataUrl) {
      toast.error("Bitte Verwaltung/Vertretung unterschreiben lassen.");
      return;
    }

    const pdfWindow = openBlankWindow();
    setSigning(true);
    try {
      // Patch handover place/date FIRST (only if changed) so the backend writes
      // signature_place_date into the PDF. NO inner try/catch — if the patch
      // throws, control jumps to the outer catch and sign-dual is never called.
      const locationChanged = location !== (receipt.handover_location || "");
      const dateChanged = dateVal !== (receipt.handover_date || "");
      if (locationChanged || dateChanged) {
        await patchKeyReceipt(receipt.id, {
          handover_location: location,
          handover_date: dateVal,
        });
      }

      await signKeyReceiptDual(receipt.id, {
        signed_by_name: name,
        signature_data_url: dataUrl,
        management_signed_by_name: mName,
        management_signature_data_url: mgmtDataUrl,
      });
      toast.success("Schlüsselquittung wurde unterzeichnet.");
      // M1c-E: the signature is committed, so the tenancy's document list is
      // already stale. Signalled HERE, before the best-effort steps below, for
      // the same reason the toast is: everything after this point may fail
      // without making the signature any less real, and the archived document
      // exists either way. Exactly once per successful sign.
      onSigned?.();
      // The receipt is now signed; opening the PDF is best-effort and must not
      // mask the successful sign.
      try {
        const result = await getKeyReceiptSignedPdfUrl(receipt.id);
        if (result?.url && pdfWindow) {
          pdfWindow.location = result.url;
          toast.success("Unterschriebenes PDF wurde geöffnet.");
        } else if (!pdfWindow) {
          toast.error("Popup wurde blockiert. Bitte Popups erlauben.");
        } else {
          pdfWindow.close();
          toast.error("Unterschriebenes PDF konnte nicht geöffnet werden.");
        }
      } catch (e) {
        pdfWindow?.close();
        toast.error(e?.message || "Unterschriebenes PDF konnte nicht geöffnet werden.");
      }
      setSignOpen(false);
      // Same reason, one level further: reloading the receipt is how this panel
      // catches up, and a failed reload is a stale panel — not a failed
      // signature. Without this the outer catch would say
      // "Signatur konnte nicht gespeichert werden" about a signature the
      // backend has committed.
      try {
        await load();
      } catch (reloadError) {
        toast.error(
          reloadError?.message || "Schlüsselquittung konnte nicht neu geladen werden."
        );
      }
    } catch (err) {
      pdfWindow?.close();
      toast.error(err?.message || "Signatur konnte nicht gespeichert werden.");
    } finally {
      setSigning(false);
    }
  };

  const handleOpenSignedPdf = async () => {
    if (!receipt?.id || openingSigned) return;
    const pdfWindow = openBlankWindow();
    setOpeningSigned(true);
    try {
      const result = await getKeyReceiptSignedPdfUrl(receipt.id);
      if (!result?.url) {
        pdfWindow?.close();
        toast.error("Unterschriebenes PDF konnte nicht geöffnet werden.");
        return;
      }
      if (pdfWindow) {
        pdfWindow.location = result.url;
        toast.success("Unterschriebenes PDF wurde geöffnet.");
      } else {
        toast.error("Popup wurde blockiert. Bitte Popups erlauben.");
      }
    } catch (err) {
      pdfWindow?.close();
      toast.error(err?.message || "Unterschriebenes PDF konnte nicht geöffnet werden.");
    } finally {
      setOpeningSigned(false);
    }
  };

  const labelCls =
    "mb-2 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]";
  const btnCls =
    "rounded-[6px] border border-black/10 bg-transparent px-[10px] py-[3px] text-[10px] text-[#64748b] transition-colors hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-default disabled:opacity-50";
  const inputCls =
    "w-full rounded-[6px] border border-black/10 bg-white px-2 py-1 text-[11px] text-[#0f172a] dark:border-white/[0.1] dark:bg-[#111520] dark:text-[#eef2ff]";
  const fieldLabelCls = "mb-0.5 block text-[10px] text-[#64748b] dark:text-[#8090b0]";

  return (
    <div className="mt-3">
      <p className={labelCls}>Schlüsselquittung</p>

      {receipt === undefined ? (
        <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">Wird geladen …</p>
      ) : receipt === null ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">
            Noch keine Schlüsselquittung erstellt.
          </p>
          <button type="button" onClick={handleCreate} disabled={isCreating} className={btnCls}>
            {isCreating ? "Wird erstellt …" : "Schlüsselquittung erstellen"}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-[8px] border border-black/10 px-3 py-3 dark:border-white/[0.08]">
          {/* Status + snapshot master data */}
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#0f172a] dark:text-[#eef2ff]">
            <span className="inline-flex rounded-full border border-black/10 bg-slate-100 px-[6px] py-[1px] text-[9px] font-semibold text-[#64748b] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#8090b0]">
              {statusLabel(receipt.status)}
            </span>
            {receipt.snapshot_tenancy_reference ? (
              <span className="font-mono text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                {receipt.snapshot_tenancy_reference}
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[#0f172a] dark:text-[#eef2ff]">
            <div><span className="text-[#64748b] dark:text-[#8090b0]">Mieter/in: </span>{receipt.snapshot_tenant_name || "—"}</div>
            <div><span className="text-[#64748b] dark:text-[#8090b0]">Mietbeginn: </span>{receipt.snapshot_tenancy_start_date ? formatDateOnly(receipt.snapshot_tenancy_start_date) : "—"}</div>
            <div><span className="text-[#64748b] dark:text-[#8090b0]">Adresse: </span>{receipt.snapshot_unit_address || "—"}</div>
            <div><span className="text-[#64748b] dark:text-[#8090b0]">PLZ / Ort: </span>{[receipt.snapshot_unit_postal_code, receipt.snapshot_unit_city].filter(Boolean).join(" ") || "—"}</div>
            <div><span className="text-[#64748b] dark:text-[#8090b0]">Zimmer: </span>{receipt.snapshot_room_name || "—"}</div>
          </div>

          {/* Metadata editor */}
          {!isSigned ? (
            <div className="rounded-[6px] border border-black/10 p-2 dark:border-white/[0.08]">
              <p className={labelCls}>Angaben</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={fieldLabelCls}>Quittungsdatum</label>
                  <input type="date" className={inputCls} value={meta.receipt_date}
                    onChange={(e) => setMeta((m) => ({ ...m, receipt_date: e.target.value }))} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Übergabedatum</label>
                  <input type="date" className={inputCls} value={meta.handover_date}
                    onChange={(e) => setMeta((m) => ({ ...m, handover_date: e.target.value }))} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Übergabeort</label>
                  <input type="text" className={inputCls} value={meta.handover_location}
                    onChange={(e) => setMeta((m) => ({ ...m, handover_location: e.target.value }))} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Geburtsdatum auf Quittung</label>
                  <input type="date" className={inputCls} value={meta.snapshot_tenant_birthdate}
                    onChange={(e) => setMeta((m) => ({ ...m, snapshot_tenant_birthdate: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className={fieldLabelCls}>Notizen</label>
                  <textarea rows={2} className={inputCls} value={meta.notes}
                    onChange={(e) => setMeta((m) => ({ ...m, notes: e.target.value }))} />
                </div>
              </div>
              <button type="button" onClick={handleSaveMeta} disabled={savingMeta} className={`${btnCls} mt-2`}>
                {savingMeta ? "Wird gespeichert …" : "Speichern"}
              </button>
            </div>
          ) : null}

          {/* Key items */}
          <div className="rounded-[6px] border border-black/10 p-2 dark:border-white/[0.08]">
            <p className={labelCls}>Schlüsselpositionen</p>
            {items.length === 0 ? (
              <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">Noch keine Positionen.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {items.map((it) => (
                  <li key={it.id} className="flex items-center justify-between gap-2 text-[11px] text-[#0f172a] dark:text-[#eef2ff]">
                    <span className="min-w-0">
                      <span className="font-medium">{keyTypeLabel(it.key_type)}</span>
                      {it.label ? <span className="text-[#64748b] dark:text-[#8090b0]"> · {it.label}</span> : null}
                      <span className="text-[#64748b] dark:text-[#8090b0]"> · Anzahl {it.quantity}</span>
                      {it.key_number ? <span className="text-[#64748b] dark:text-[#8090b0]"> · {it.key_number}</span> : null}
                      {it.notes ? <span className="text-[#64748b] dark:text-[#6b7a9a]"> · {it.notes}</span> : null}
                    </span>
                    {!isSigned ? (
                      <button type="button" onClick={() => handleDeleteItem(it.id)} disabled={deletingItemId === it.id}
                        className="shrink-0 rounded-[6px] border border-rose-500/30 bg-transparent px-[8px] py-[2px] text-[10px] text-rose-600 transition-colors hover:bg-rose-50 dark:border-rose-400/30 dark:text-rose-400 dark:hover:bg-rose-950/30 disabled:opacity-50">
                        {deletingItemId === it.id ? "…" : "Löschen"}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}

            {!isSigned ? (
              <div className="mt-2 grid grid-cols-2 gap-2 border-t border-black/10 pt-2 dark:border-white/[0.08]">
                <div>
                  <label className={fieldLabelCls}>Schlüsselart</label>
                  <select className={inputCls} value={itemForm.key_type}
                    onChange={(e) => setItemForm((f) => ({ ...f, key_type: e.target.value }))}>
                    {KEY_TYPE_OPTIONS.map(([val, lab]) => (
                      <option key={val} value={val}>{lab}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={fieldLabelCls}>Bezeichnung</label>
                  <input type="text" className={inputCls} value={itemForm.label}
                    onChange={(e) => setItemForm((f) => ({ ...f, label: e.target.value }))} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Anzahl</label>
                  <input type="number" min="1" className={inputCls} value={itemForm.quantity}
                    onChange={(e) => setItemForm((f) => ({ ...f, quantity: e.target.value }))} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Schlüsselnummer / Badge</label>
                  <input type="text" className={inputCls} value={itemForm.key_number}
                    onChange={(e) => setItemForm((f) => ({ ...f, key_number: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className={fieldLabelCls}>Bemerkung</label>
                  <input type="text" className={inputCls} value={itemForm.notes}
                    onChange={(e) => setItemForm((f) => ({ ...f, notes: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <button type="button" onClick={handleAddItem} disabled={addingItem} className={btnCls}>
                    {addingItem ? "Wird hinzugefügt …" : "Position hinzufügen"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {isSigned ? (
            /* Signed → final. Signed PDF is the primary action; the normal PDF
               and all editors are gone (CRUD/regenerate are 409 on the backend). */
            <div className="rounded-[6px] border border-emerald-500/30 bg-emerald-50/60 p-2 dark:border-emerald-400/20 dark:bg-emerald-950/20">
              <p className={labelCls}>Unterzeichnet</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11px] text-[#0f172a] dark:text-[#eef2ff]">
                <div>
                  <div className="text-[10px] font-semibold text-[#64748b] dark:text-[#8090b0]">Mieter/in</div>
                  <div>{receipt.signed_by_name || "—"}</div>
                  <div className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    {receipt.signed_at ? formatDateTime(receipt.signed_at) : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-[#64748b] dark:text-[#8090b0]">Verwaltung/Vertretung</div>
                  <div>{receipt.management_signed_by_name || "—"}</div>
                  <div className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    {receipt.management_signed_at ? formatDateTime(receipt.management_signed_at) : "—"}
                  </div>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {hasSignedPdf ? (
                  <button type="button" onClick={handleOpenSignedPdf} disabled={openingSigned} className={btnCls}>
                    {openingSigned ? "Öffnet …" : "Unterschriebenes PDF öffnen"}
                  </button>
                ) : (
                  <span className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Unterschriebenes PDF ist noch nicht verfügbar.
                  </span>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* PDF actions (draft/generated) */}
              <div className="flex flex-wrap items-center gap-2">
                {hasPdf ? (
                  <>
                    <button type="button" onClick={handleOpenPdf} disabled={opening || pdfBusy} className={btnCls}>
                      {opening ? "Öffnet …" : "PDF öffnen"}
                    </button>
                    <button type="button" onClick={() => handleGenerateAndOpen(true)} disabled={pdfBusy} className={btnCls}>
                      {pdfBusy ? "Generiert …" : "PDF neu generieren"}
                    </button>
                    {receipt.pdf_generated_at ? (
                      <span className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Generiert am {formatDateOnly(receipt.pdf_generated_at)}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => handleGenerateAndOpen(false)}
                      disabled={pdfBusy || items.length === 0} className={btnCls}>
                      {pdfBusy ? "Generiert …" : "PDF generieren"}
                    </button>
                    {items.length === 0 ? (
                      <span className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Mindestens eine Schlüsselposition erforderlich.
                      </span>
                    ) : null}
                  </>
                )}
              </div>

              {/* Signature (K2c) */}
              <div className="rounded-[6px] border border-black/10 p-2 dark:border-white/[0.08]">
                {!signOpen ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={openSignPanel} disabled={items.length === 0} className={btnCls}>
                      Unterschreiben
                    </button>
                    {items.length === 0 ? (
                      <span className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Mindestens eine Schlüsselposition erforderlich.
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="text-[12px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                      Schlüsselquittung unterschreiben
                    </p>
                    <div className="rounded-[6px] border border-amber-500/40 bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800 dark:border-amber-400/30 dark:bg-amber-950/30 dark:text-amber-300">
                      Nach dem Unterschreiben kann die Quittung nicht mehr geändert werden.
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={fieldLabelCls}>Ort der Übergabe</label>
                        <input type="text" className={inputCls} value={signLocation} disabled={signing}
                          onChange={(e) => setSignLocation(e.target.value)} />
                      </div>
                      <div>
                        <label className={fieldLabelCls}>Datum der Übergabe</label>
                        <input type="date" className={inputCls} value={signDate} disabled={signing}
                          onChange={(e) => setSignDate(e.target.value)} />
                      </div>
                    </div>
                    <p className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Für eine bessere Unterschrift das Gerät quer halten.
                    </p>

                    {/* Mieter/in */}
                    <div className="rounded-[6px] border border-black/10 p-2 dark:border-white/[0.08]">
                      <p className={labelCls}>Mieter/in</p>
                      <label className={fieldLabelCls}>Name Mieter/in</label>
                      <input type="text" className={inputCls} value={signName} disabled={signing}
                        onChange={(e) => setSignName(e.target.value)} />
                      <div className="mt-2">
                        <SignaturePad ref={sigPadRef} height={180} onEmptyChange={setSigEmpty} />
                      </div>
                      <button type="button" onClick={() => sigPadRef.current?.clear()} disabled={signing} className={`${btnCls} mt-2`}>
                        Mieter-Signatur zurücksetzen
                      </button>
                    </div>

                    {/* Verwaltung/Vertretung */}
                    <div className="rounded-[6px] border border-black/10 p-2 dark:border-white/[0.08]">
                      <p className={labelCls}>Verwaltung/Vertretung</p>
                      <label className={fieldLabelCls}>Name Verwaltung/Vertretung</label>
                      <input type="text" className={inputCls} value={mgmtName} disabled={signing}
                        onChange={(e) => setMgmtName(e.target.value)} />
                      <div className="mt-2">
                        <SignaturePad ref={mgmtSigPadRef} height={180} onEmptyChange={setMgmtSigEmpty} />
                      </div>
                      <button type="button" onClick={() => mgmtSigPadRef.current?.clear()} disabled={signing} className={`${btnCls} mt-2`}>
                        Verwaltungs-Signatur zurücksetzen
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={closeSignPanel} disabled={signing} className={btnCls}>
                        Abbrechen
                      </button>
                      <button type="button" onClick={handleSign} disabled={!canSign}
                        className="rounded-[6px] border border-emerald-600/40 bg-emerald-600 px-[10px] py-[3px] text-[10px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-default disabled:opacity-50">
                        {signing ? "Wird unterzeichnet …" : "Unterschreiben & PDF erstellen"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {listError ? (
        <p className="mt-1 text-[10px] text-red-600 dark:text-red-400">{listError}</p>
      ) : null}
    </div>
  );
}
